import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type TransitionEvent,
} from 'react';
import './CardCarousel.css';
import {
  activeIndexFromTime,
  loopPeriodSeconds,
  normalizeCards,
  windowStartFromActive,
  wrapInternalTime,
} from './timing';
import type { CardCarouselProps, CardItem } from './types';

const DEFAULT_SLIDE_MS = 650;
const DEFAULT_VISIBLE = 3;
const DEFAULT_GAP = 12;
const DEFAULT_RADIUS = 18;
const DEFAULT_ACTIVE_SCALE = 1;
const DEFAULT_INACTIVE_OPACITY = 0.9;

function cssSize(value: number | string | undefined, fallback: string): string {
  if (value == null || value === '') return fallback;
  return typeof value === 'number' ? `${value}px` : String(value);
}

/**
 * Horizontal card strip for video overlays.
 *
 * Active-card priority:
 * 1. `activeIndex` when the prop is supplied (including `0`)
 * 2. `currentTime` from a parent video / clock
 * 3. Internal timestamp timer (`isPlaying` pauses that timer)
 */
export function CardCarousel({
  cards,
  currentTime,
  activeIndex,
  onActiveIndexChange,
  slideDuration = DEFAULT_SLIDE_MS,
  cardWidth,
  cardHeight,
  gap = DEFAULT_GAP,
  visibleCards = DEFAULT_VISIBLE,
  position = 'bottom',
  offsetY = 0,
  borderRadius = DEFAULT_RADIUS,
  activeScale = DEFAULT_ACTIVE_SCALE,
  inactiveOpacity = DEFAULT_INACTIVE_OPACITY,
  loop = false,
  isPlaying = true,
  className = '',
  style,
}: CardCarouselProps) {
  const normalized = useMemo(() => normalizeCards(cards), [cards]);
  const visible = Math.max(1, Math.round(Number(visibleCards) || DEFAULT_VISIBLE));
  const durationMs = Math.max(80, Number(slideDuration) || DEFAULT_SLIDE_MS);
  const controlledByIndex = activeIndex !== undefined && activeIndex !== null;
  const controlledByTime = !controlledByIndex && currentTime !== undefined && currentTime !== null;

  const [internalIndex, setInternalIndex] = useState(0);
  const [trackIndex, setTrackIndex] = useState(0);
  const [snapTrack, setSnapTrack] = useState(false);

  const elapsedRef = useRef(0);
  const runningRef = useRef(false);
  const lastTickRef = useRef(0);
  const lastIndexRef = useRef(0);
  const pendingWrapRef = useRef(false);
  const trackIndexRef = useRef(0);
  const notifyRef = useRef(onActiveIndexChange);
  notifyRef.current = onActiveIndexChange;

  const resolvedActive = useMemo(() => {
    if (!normalized.length) return 0;
    if (controlledByIndex) {
      const max = loop ? normalized.length : Math.max(0, normalized.length - 1);
      return Math.min(max, Math.max(0, Math.round(Number(activeIndex) || 0)));
    }
    if (controlledByTime) {
      const period = loopPeriodSeconds(normalized);
      const t = loop
        ? wrapInternalTime(Number(currentTime) || 0, period)
        : Number(currentTime) || 0;
      return activeIndexFromTime(normalized, t);
    }
    return Math.min(Math.max(0, internalIndex), Math.max(0, normalized.length - 1));
  }, [
    normalized,
    controlledByIndex,
    controlledByTime,
    activeIndex,
    currentTime,
    internalIndex,
    loop,
  ]);

  const windowStart = windowStartFromActive(
    resolvedActive,
    normalized.length,
    visible,
    loop,
  );

  useEffect(() => {
    if (lastIndexRef.current === resolvedActive) return;
    lastIndexRef.current = resolvedActive;
    notifyRef.current?.(resolvedActive);
  }, [resolvedActive]);

  useEffect(() => {
    if (!loop) {
      pendingWrapRef.current = false;
      trackIndexRef.current = windowStart;
      setTrackIndex(windowStart);
      return;
    }
    const n = normalized.length;
    const prev = trackIndexRef.current;
    if (n > 0 && prev === n && windowStart === 0) {
      return;
    }
    if (n > 1 && prev > 0 && windowStart === 0 && prev >= Math.max(1, n - visible)) {
      pendingWrapRef.current = true;
      trackIndexRef.current = n;
      setTrackIndex(n);
      return;
    }
    trackIndexRef.current = windowStart;
    setTrackIndex(windowStart);
  }, [windowStart, loop, normalized.length, visible]);

  const onTrackTransitionEnd = useCallback((event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'transform') return;
    if (!pendingWrapRef.current) return;
    pendingWrapRef.current = false;
    trackIndexRef.current = 0;
    setSnapTrack(true);
    setTrackIndex(0);
  }, []);

  useEffect(() => {
    if (!snapTrack) return undefined;
    const id = window.requestAnimationFrame(() => {
      setSnapTrack(false);
    });
    return () => window.cancelAnimationFrame(id);
  }, [snapTrack]);

  useEffect(() => {
    if (controlledByIndex || controlledByTime || !normalized.length) {
      runningRef.current = false;
      return undefined;
    }
    if (!isPlaying) {
      runningRef.current = false;
      lastTickRef.current = 0;
      return undefined;
    }

    runningRef.current = true;
    lastTickRef.current = 0;
    let raf = 0;
    const period = loopPeriodSeconds(normalized);

    const tick = (now: number) => {
      if (!runningRef.current) return;
      if (!lastTickRef.current) lastTickRef.current = now;
      const delta = Math.max(0, (now - lastTickRef.current) / 1000);
      lastTickRef.current = now;
      elapsedRef.current += delta;
      const t = loop
        ? wrapInternalTime(elapsedRef.current, period)
        : elapsedRef.current;
      if (loop && elapsedRef.current >= period) {
        elapsedRef.current = wrapInternalTime(elapsedRef.current, period);
      }
      const next = activeIndexFromTime(normalized, t);
      setInternalIndex((prev) => (prev === next ? prev : next));
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => {
      runningRef.current = false;
      window.cancelAnimationFrame(raf);
    };
  }, [controlledByIndex, controlledByTime, isPlaying, normalized, loop]);

  useEffect(() => () => {
    runningRef.current = false;
  }, []);

  const renderedCards: CardItem[] = useMemo(() => {
    if (!normalized.length) return [];
    if (!loop) return normalized;
    return normalized.concat(normalized.slice(0, visible));
  }, [normalized, loop, visible]);

  const stride = `calc(100% / ${visible} + ${Math.max(0, Number(gap) || 0)}px / ${visible})`;
  const translate = `calc(-1 * ${trackIndex} * (${stride}))`;

  const rootStyle = {
    ...style,
    '--cc-gap': `${Math.max(0, Number(gap) || 0)}px`,
    '--cc-radius': `${Math.max(0, Number(borderRadius) || 0)}px`,
    '--cc-duration': `${durationMs}ms`,
    '--cc-visible': String(visible),
    '--cc-card-w': cssSize(
      cardWidth,
      `calc((100% - ${(visible - 1)} * ${Math.max(0, Number(gap) || 0)}px) / ${visible})`,
    ),
    '--cc-card-h': cssSize(cardHeight, '38cqh'),
  } as CSSProperties;

  const bandShift = {
    transform: position === 'center'
      ? `translateY(calc(-50% + ${offsetY}px))`
      : `translateY(${offsetY}px)`,
  };

  return (
    <div
      className={`card-carousel ${className}`.trim()}
      style={rootStyle}
      data-testid="card-carousel"
      aria-hidden={normalized.length === 0}
    >
      <div
        className={`card-carousel__band card-carousel__band--${position}`}
        style={bandShift}
      >
        <div className="card-carousel__viewport">
          <div
            className={`card-carousel__track${snapTrack ? ' card-carousel__track--snap' : ''}`}
            style={{ transform: `translate3d(${translate}, 0, 0)` }}
            onTransitionEnd={onTrackTransitionEnd}
          >
            {renderedCards.map((card, index) => {
              const logical = normalized.length ? index % normalized.length : 0;
              const isActive = logical === resolvedActive;
              return (
                <article
                  key={`${card.title}-${index}`}
                  className="card-carousel__card"
                  style={{
                    ['--cc-scale' as string]: isActive ? activeScale : 1,
                    ['--cc-opacity' as string]: isActive ? 1 : inactiveOpacity,
                  } as CSSProperties}
                  data-active={isActive ? 'true' : 'false'}
                >
                  {card.image ? (
                    <img
                      className="card-carousel__image"
                      src={card.image}
                      alt={card.title}
                      draggable={false}
                    />
                  ) : (
                    <div className="card-carousel__image-fallback" />
                  )}
                  {card.title ? (
                    <p className="card-carousel__title">{card.title}</p>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CardCarousel;
